import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X, ChevronRight } from "lucide-react";
import { fetchMapSearch } from "../../api/map";
import type { MapSearchResult } from "../../api/map";

// Mobile-only global map search, ported from the app's explore screen: a
// round glass bubble that expands into a full-width field with a results
// card underneath. Desktop keeps the sidebar search in RegionOverview.

function statusColor(status: string): string {
  if (status === "collected") return "var(--gold-400)";
  if (status === "in_progress") return "var(--sky-400)";
  return "var(--text-muted)";
}

function resultMeta(r: MapSearchResult): string {
  if (r.type === "peak") return r.elevation != null ? `Peak · ${r.elevation} m` : "Peak";
  if (r.type === "trail") return r.distanceKm != null ? `Trail · ${r.distanceKm} km` : "Trail";
  return r.category ? `Landmark · ${r.category.replace(/_/g, " ")}` : "Landmark";
}

export function MobileMapSearchBubble({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      title="Search the map"
      aria-label="Search the map"
      className="atlas-glass"
      style={{
        width: 40,
        height: 40,
        flex: "0 0 auto",
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        borderRadius: "var(--radius-pill)",
        color: "var(--text-secondary)",
        boxShadow: "var(--shadow-md)",
        pointerEvents: "auto",
      }}
    >
      <Search size={16} />
    </button>
  );
}

export function MobileMapSearchField({
  onClose,
  onSelect,
}: {
  onClose: () => void;
  onSelect: (r: MapSearchResult) => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const searching = debounced.length >= 2;
  const searchQuery = useQuery({
    queryKey: ["map/search", debounced],
    queryFn: () => fetchMapSearch(debounced),
    enabled: searching,
    staleTime: 60_000,
  });
  const results = searching ? (searchQuery.data?.results ?? []) : [];

  return (
    <div style={{ flex: 1, minWidth: 0, position: "relative", pointerEvents: "auto" }}>
      <div
        className="atlas-glass"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 44,
          padding: "0 12px",
          borderRadius: "var(--radius-pill)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <Search size={15} color="var(--text-muted)" style={{ flex: "0 0 auto" }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search peaks, trails, landmarks"
          aria-label="Search all features by name"
          autoFocus
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            background: "transparent",
            outline: "none",
            color: "var(--text-primary)",
            fontSize: 14,
            font: "inherit",
          }}
        />
        <button
          onClick={onClose}
          title="Close search"
          aria-label="Close search"
          style={{
            width: 26,
            height: 26,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            border: "none",
            borderRadius: "var(--radius-pill)",
            background: "transparent",
            color: "var(--text-secondary)",
          }}
        >
          <X size={15} />
        </button>
      </div>

      {searching && (
        <div
          className="atlas-glass"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-lg)",
            maxHeight: 320,
            overflowY: "auto",
            zIndex: 30,
          }}
        >
          {results.length === 0 && !searchQuery.isFetching ? (
            <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
              No matches
            </div>
          ) : (
            results.map((r) => (
              <button
                key={`${r.type}-${r.id}`}
                onClick={() => onSelect(r)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  padding: "10px 14px",
                  border: "none",
                  background: "transparent",
                  font: "inherit",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    flex: "0 0 auto",
                    borderRadius: "50%",
                    background: statusColor(r.status),
                  }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: "var(--text-primary)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.name}
                  </span>
                  <span className="eyebrow" style={{ display: "block", fontSize: 9, marginTop: 2 }}>
                    {resultMeta(r)}
                  </span>
                </span>
                <ChevronRight size={14} color="var(--text-faint)" style={{ flex: "0 0 auto" }} />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
